
import { useState, useEffect } from "react"
import { useParams, Link } from "react-router-dom"
import { Star, Truck, ShieldCheck, RotateCcw, Heart, Share2, Minus, Plus, ShoppingCart } from "lucide-react"
import Breadcrumbs from "../../components/goods/bread-crumb"
import { useCart } from "../../context/cart-context"

// Mock product data - would be replaced with API call
import PremiumStand from "../../assets/goods/flash-sales/premium-stand-mixer.webp"
import Smart_Air_Fryer from "../../assets/goods/flash-sales/premium-air-fryer.webp"
import Cordless_Drill from "../../assets/goods/flash-sales/premium-cordless.webp"
import Portable_ac from "../../assets/goods/flash-sales/premium-portable-ac.webp"

const products = [
  {
    id: "1",
    name: "Premium Stand Mixer",
    image: PremiumStand,
    price: 159.99,
    originalPrice: 220.0,
    rating: 4.5,
    reviewCount: 3654,
    discount: 25,
    description:
      "This premium stand mixer is perfect for all your baking needs. With a powerful motor and multiple attachments, it can handle everything from kneading dough to whipping cream.",
    features: [
      "Powerful 500W motor",
      "10 speed settings",
      "5.5 quart stainless steel bowl",
      "Includes dough hook, flat beater, and wire whip",
      "Tilt-head design for easy access to bowl",
    ],
    specifications: {
      Brand: "KitchenPro",
      Model: "KSM150",
      Color: "Silver",
      Material: "Metal",
      Capacity: "5.5 quarts",
      Power: "500W",
      Dimensions: "14.1 x 8.7 x 14 inches",
      Weight: "25 pounds",
      Warranty: "1 year",
    },
    stock: 15,
    category: "Kitchen Appliances",
    images: [PremiumStand, PremiumStand, PremiumStand, PremiumStand],
  },
  {
    id: "2",
    name: "Smart Air Fryer",
    image: Smart_Air_Fryer,
    price: 89.99,
    originalPrice: 129.99,
    rating: 4.7,
    reviewCount: 1254,
    discount: 30,
    description:
      "Cook healthier meals with this smart air fryer. Using hot air circulation technology, it requires little to no oil while still delivering crispy, delicious results.",
    features: [
      "Digital touchscreen display",
      "8 preset cooking programs",
      "5.5 quart capacity",
      "Temperature range: 180°F to 400°F",
      "60-minute timer with auto shut-off",
    ],
    specifications: {
      Brand: "AirChef",
      Model: "AC550",
      Color: "Black",
      Material: "Plastic/Metal",
      Capacity: "5.5 quarts",
      Power: "1500W",
      Dimensions: "11 x 11 x 12.5 inches",
      Weight: "12 pounds",
      Warranty: "2 years",
    },
    stock: 8,
    category: "Kitchen Appliances",
    images: [Smart_Air_Fryer, Smart_Air_Fryer, Smart_Air_Fryer, Smart_Air_Fryer],
  },
  {
    id: "3",
    name: "Cordless Power Drill",
    image: Cordless_Drill,
    price: 69.99,
    originalPrice: 99.99,
    rating: 4.8,
    reviewCount: 2345,
    discount: 30,
    description:
      "This powerful cordless drill is perfect for DIY projects and professional use. With a long-lasting battery and ergonomic design, it makes drilling and driving screws effortless.",
    features: [
      "20V lithium-ion battery",
      "2-speed gearbox (0-450/0-1500 RPM)",
      "1/2-inch keyless chuck",
      "LED work light",
      "Includes 2 batteries, charger, and carrying case",
    ],
    specifications: {
      Brand: "PowerPro",
      Model: "PP20V",
      Color: "Yellow/Black",
      Material: "Plastic/Metal",
      Battery: "20V Lithium-Ion",
      Torque: "50 Nm",
      Dimensions: "8 x 3 x 8.5 inches",
      Weight: "3.5 pounds",
      Warranty: "3 years",
    },
    stock: 20,
    category: "Electric Tools",
    images: [Cordless_Drill, Cordless_Drill, Cordless_Drill, Cordless_Drill],
  },
  {
    id: "4",
    name: "Portable AC Unit",
    image: Portable_ac,
    price: 249.99,
    originalPrice: 329.99,
    rating: 4.2,
    reviewCount: 567,
    discount: 25,
    description:
      "Stay cool anywhere with this portable air conditioner. Easy to move from room to room, it provides efficient cooling without the need for permanent installation.",
    features: [
      "8,000 BTU cooling capacity",
      "Cools rooms up to 350 sq. ft.",
      "Digital controls with remote",
      "24-hour programmable timer",
      "Dehumidifier function",
    ],
    specifications: {
      Brand: "CoolBreeze",
      Model: "CB8000",
      Color: "White",
      Material: "Plastic",
      "Cooling Capacity": "8,000 BTU",
      Power: "900W",
      Dimensions: "17.5 x 14.6 x 28.9 inches",
      Weight: "55 pounds",
      Warranty: "1 year",
    },
    stock: 5,
    category: "AC & Cooling",
    images: [Portable_ac, Portable_ac, Portable_ac, Portable_ac],
  },
]

export default function ProductDetailPage() {
  const { productId } = useParams()
  const [product, setProduct] = useState(null)
  const [loading, setLoading] = useState(true)
  const [selectedImage, setSelectedImage] = useState(0)
  const [quantity, setQuantity] = useState(1)
  const { addToCart } = useCart()

  useEffect(() => {
    // Simulate API call to fetch product details
    setLoading(true)
    setTimeout(() => {
      const foundProduct = products.find((p) => p.id === productId)
      setProduct(foundProduct || null)
      setLoading(false)
    }, 500)
  }, [productId])

  const handleAddToCart = () => {
    if (product) {
      addToCart({
        id: product.id,
        name: product.name,
        image: product.image,
        price: product.price,
        quantity,
      })
    }
  }

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="animate-pulse">
          <div className="h-4 bg-gray-200 rounded w-1/4 mb-6"></div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="h-96 bg-gray-200 rounded"></div>
            <div>
              <div className="h-8 bg-gray-200 rounded w-3/4 mb-4"></div>
              <div className="h-4 bg-gray-200 rounded w-1/4 mb-6"></div>
              <div className="h-4 bg-gray-200 rounded w-full mb-2"></div>
              <div className="h-4 bg-gray-200 rounded w-full mb-2"></div>
              <div className="h-4 bg-gray-200 rounded w-3/4 mb-6"></div>
              <div className="h-10 bg-gray-200 rounded w-1/3 mb-4"></div>
              <div className="h-12 bg-gray-200 rounded w-full mb-4"></div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  if (!product) {
    return (
      <div className="container mx-auto px-4 py-8 text-center">
        <h2 className="text-2xl font-bold mb-4">Product Not Found</h2>
        <p className="mb-6">The product you're looking for doesn't exist or has been removed.</p>
        <Link to="/goods" className="bg-[#ff9906] text-white px-4 py-2 rounded-md hover:bg-[#e68a00] transition-colors">
          Return to Home
        </Link>
      </div>
    )
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <Breadcrumbs
        items={[
          { label: "Products", href: "/goods" },
          {
            label: product.category,
            href: `/goods/category/${product.category.toLowerCase().replace(/['\s&]+/g, "-")}`,
          },
          { label: product.name },
        ]}
      />

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        {/* Product Images */}
        <div className="space-y-4">
          <div className="border rounded-lg overflow-hidden h-96">
            <img
              src={product.images[selectedImage] || "/placeholder.svg"}
              alt={product.name}
              className="w-full h-full object-contain"
            />
          </div>

          <div className="grid grid-cols-4 gap-2">
            {product.images.map((image, index) => (
              <button
                key={index}
                className={`border rounded-md overflow-hidden h-20 ${selectedImage === index ? "border-[#ff9906] ring-1 ring-[#ff9906]" : "border-gray-200"}`}
                onClick={() => setSelectedImage(index)}
              >
                <img
                  src={image || "/placeholder.svg"}
                  alt={`${product.name} - view ${index + 1}`}
                  className="w-full h-full object-contain"
                />
              </button>
            ))}
          </div>
        </div>

        {/* Product Info */}
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-gray-800 mb-2">{product.name}</h1>

          {/* Rating */}
          <div className="flex items-center mb-4">
            <div className="flex items-center">
              {[...Array(5)].map((_, i) => (
                <Star
                  key={i}
                  size={16}
                  className={i < Math.floor(product.rating) ? "fill-[#ff9906] text-[#ff9906]" : "text-gray-300"}
                />
              ))}
            </div>
            <span className="ml-2 text-sm text-gray-500">
              {product.rating} ({product.reviewCount} reviews)
            </span>
          </div>

          {/* Price */}
          <div className="mb-6">
            <div className="flex items-center">
              <span className="text-2xl font-bold text-[#ff9906]">${product.price.toFixed(2)}</span>
              {product.originalPrice && (
                <span className="ml-2 text-gray-400 text-lg line-through">${product.originalPrice.toFixed(2)}</span>
              )}
              {product.discount && (
                <span className="ml-2 bg-red-100 text-red-800 text-xs font-medium px-2 py-0.5 rounded">
                  Save {product.discount}%
                </span>
              )}
            </div>
            <p className="text-sm text-gray-500 mt-1">
              {product.stock > 0 ? (
                <>
                  <span className="text-green-600 font-medium">In Stock</span>
                  {product.stock < 10 && ` (Only ${product.stock} left)`}
                </>
              ) : (
                <span className="text-red-600 font-medium">Out of Stock</span>
              )}
            </p>
          </div>

          {/* Description */}
          <p className="text-gray-600 mb-6">{product.description}</p>

          {/* Quantity Selector */}
          <div className="flex items-center space-x-4 mb-6">
            <span className="text-gray-700">Quantity:</span>
            <div className="flex items-center border border-gray-300 rounded-md">
              <button
                onClick={() => setQuantity(Math.max(1, quantity - 1))}
                disabled={quantity <= 1}
                className="px-3 py-1 text-gray-600 hover:bg-gray-100 disabled:opacity-50"
              >
                <Minus size={16} />
              </button>

              <input
                type="text"
                value={quantity}
                onChange={(e) => {
                  const val = Number.parseInt(e.target.value)
                  if (!isNaN(val) && val > 0 && val <= product.stock) {
                    setQuantity(val)
                  }
                }}
                className="w-12 text-center border-none focus:outline-none"
              />

              <button
                onClick={() => setQuantity(Math.min(product.stock, quantity + 1))}
                disabled={quantity >= product.stock}
                className="px-3 py-1 text-gray-600 hover:bg-gray-100 disabled:opacity-50"
              >
                <Plus size={16} />
              </button>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 mb-8">
            <button
              onClick={handleAddToCart}
              disabled={product.stock <= 0}
              className="flex-1 bg-[#ff9906] hover:bg-[#e68a00] text-white py-3 px-6 rounded-md font-medium flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ShoppingCart size={20} />
              Add to Cart
            </button>

            <button className="flex-1 border border-gray-300 hover:bg-gray-50 text-gray-700 py-3 px-6 rounded-md font-medium flex items-center justify-center gap-2">
              <Heart size={20} />
              Add to Wishlist
            </button>
          </div>

          {/* Shipping Info */}
          <div className="space-y-3 border-t border-gray-200 pt-6">
            <div className="flex items-start gap-3">
              <Truck className="text-[#ff9906] mt-0.5" size={20} />
              <div>
                <p className="font-medium text-gray-800">Free Shipping</p>
                <p className="text-sm text-gray-500">Free standard shipping on orders over $100</p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <ShieldCheck className="text-[#ff9906] mt-0.5" size={20} />
              <div>
                <p className="font-medium text-gray-800">Warranty</p>
                <p className="text-sm text-gray-500">{product.specifications.Warranty} manufacturer warranty</p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <RotateCcw className="text-[#ff9906] mt-0.5" size={20} />
              <div>
                <p className="font-medium text-gray-800">30-Day Returns</p>
                <p className="text-sm text-gray-500">Shop with confidence</p>
              </div>
            </div>
          </div>

          {/* Share */}
          <div className="flex items-center gap-4 mt-6 pt-6 border-t border-gray-200">
            <span className="text-gray-700">Share:</span>
            <button className="text-gray-500 hover:text-[#ff9906]">
              <Share2 size={18} />
            </button>
          </div>
        </div>
      </div>

      {/* Product Details Tabs */}
      <div className="mt-12">
        <div className="border-b border-gray-200">
          <nav className="flex space-x-8">
            <button className="border-b-2 border-[#ff9906] py-4 px-1 text-[#ff9906] font-medium">Description</button>
            <button className="border-b-2 border-transparent py-4 px-1 text-gray-500 hover:text-gray-700 font-medium">
              Specifications
            </button>
            <button className="border-b-2 border-transparent py-4 px-1 text-gray-500 hover:text-gray-700 font-medium">
              Reviews
            </button>
          </nav>
        </div>

        <div className="py-6">
          <h3 className="text-lg font-medium mb-4">Features</h3>
          <ul className="list-disc pl-5 space-y-2 text-gray-600">
            {product.features.map((feature, index) => (
              <li key={index}>{feature}</li>
            ))}
          </ul>

          <h3 className="text-lg font-medium mt-8 mb-4">Specifications</h3>
          <div className="border rounded-lg overflow-hidden">
            <table className="min-w-full divide-y divide-gray-200">
              <tbody className="divide-y divide-gray-200">
                {Object.entries(product.specifications).map(([key, value], index) => (
                  <tr key={index} className={index % 2 === 0 ? "bg-gray-50" : "bg-white"}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{key}</td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{value}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  )
}
